import { PublicReadUnavailable, fetchPublicRead } from "@/lib/public-read";
import { auth } from "@lyder-talk/core/lib/auth-client";
import { apiGet, apiPost } from "@lyder-talk/core/lib/api-client";

/**
 * User data helper utilities for batch fetching user emails and avatars
 */

// Type assertion helper for RPC functions not yet in generated types
type UnknownRpcFunction = "get_batch_user_data" | "get_user_avatar";

export interface UserData {
  user_id: string;
  email: string;
  avatar_url: string | null;
  display_name: string;
}

export interface UserDataMap {
  emails: Record<string, string>;
  avatars: Record<string, string | null>;
  display_names: Record<string, string>;
}

/**
 * Rows for a batch of users, from the cached read endpoint when possible.
 *
 * Returns `null` when the direct RPC itself failed (as opposed to returning no
 * rows), which the caller turns into the "Unknown" placeholders.
 */
async function fetchUserDataRows(uniqueIds: string[]): Promise<UserData[] | null> {
  try {
    const cached = await fetchPublicRead<{ data: UserData[] }>("profiles", { ids: uniqueIds });
    return Array.isArray(cached.data) ? cached.data : [];
  } catch (error) {
    if (!(error instanceof PublicReadUnavailable)) throw error;
    console.warn("[profiles] cached read unavailable, using direct query:", error.message);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { users: userData, error } = await apiPost<{ users: unknown[] }>("/v1/users/batch", { ids: uniqueIds }).then(
      (r) => ({ users: r.users, error: null as null | Error }),
    ).catch((e: unknown) => ({ users: [], error: e instanceof Error ? e : new Error(String(e)) }));

  if (error) {
    console.error("Error fetching batch user data:", error);
    return null;
  }

  return userData && Array.isArray(userData) ? (userData as unknown as UserData[]) : [];
}

/**
 * Fetch user data (email and avatar) for multiple users in a single batch call
 * @param userIds Array of user UUIDs
 * @returns Map of user_id to UserData
 */
export async function fetchUserDataBatch(
  userIds: string[]
): Promise<Map<string, UserData>> {
  const resultMap = new Map<string, UserData>();

  if (userIds.length === 0) {
    return resultMap;
  }

  const uniqueIds = [...new Set(userIds)].filter(Boolean);
  if (uniqueIds.length === 0) {
    return resultMap;
  }

  try {
    const rows = await fetchUserDataRows(uniqueIds);

    if (rows) {
      rows.forEach((item: UserData) => {
        resultMap.set(item.user_id, {
          user_id: item.user_id,
          email: item.email || "Unknown",
          avatar_url: item.avatar_url,
          display_name: item.display_name || "Unknown",
        });
      });
    }

    // Ensure all requested user IDs have entries (fallback for missing users)
    uniqueIds.forEach((userId) => {
      if (!resultMap.has(userId)) {
        resultMap.set(userId, {
          user_id: userId,
          email: "Unknown",
          avatar_url: null,
          display_name: "Unknown",
        });
      }
    });
  } catch (err) {
    console.error("Error fetching user data in batch:", err);
    // Return fallback data for all users
    uniqueIds.forEach((userId) => {
      resultMap.set(userId, {
        user_id: userId,
        email: "Unknown",
        avatar_url: null,
        display_name: "Unknown",
      });
    });
  }

  return resultMap;
}

/**
 * Build separate email and avatar maps from user IDs
 * This is a legacy helper for code that uses separate maps
 * @param userIds Array of user UUIDs
 * @returns Object with separate emails and avatars maps
 */
export async function buildUserDataMaps(
  userIds: string[]
): Promise<UserDataMap> {
  const userDataMap = await fetchUserDataBatch(userIds);
  
  const emails: Record<string, string> = {};
  const avatars: Record<string, string | null> = {};
  const display_names: Record<string, string> = {};

  userDataMap.forEach((data, userId) => {
    emails[userId] = data.email;
    avatars[userId] = data.avatar_url;
    display_names[userId] = data.display_name;
  });

  return { emails, avatars, display_names };
}

/**
 * Fetch email for a single user
 * @param userId User UUID
 * @returns User email or "Unknown"
 */
export async function fetchUserEmail(userId: string): Promise<string> {
  if (!userId) return "Unknown";

  try {
    const { data, error } = await apiGet<{ email: string | null }>(`/v1/users/${encodeURIComponent(userId)}/email`)
      .then((r) => ({ data: r.email, error: null as null | Error }))
      .catch((e: unknown) => ({ data: null, error: e instanceof Error ? e : new Error(String(e)) }));

    if (error) {
      console.error(`Error fetching email for user ${userId}:`, error);
      return "Unknown";
    }

    return (data as string) || "Unknown";
  } catch (err) {
    console.error(`Error fetching email for user ${userId}:`, err);
    return "Unknown";
  }
}

/**
 * Fetch avatar URL for a single user
 * @param userId User UUID
 * @returns User avatar URL or null
 */
export async function fetchUserAvatar(userId: string): Promise<string | null> {
  if (!userId) return null;

  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { data, error } = await apiGet<{ avatarUrl: string | null }>(`/v1/users/${encodeURIComponent(userId)}/avatar`)
      .then((r) => ({ data: r.avatarUrl, error: null as null | Error }))
      .catch((e: unknown) => ({ data: null, error: e instanceof Error ? e : new Error(String(e)) }));

    if (error) {
      console.error(`Error fetching avatar for user ${userId}:`, error);
      return null;
    }

    return (data as string | null) || null;
  } catch (err) {
    console.error(`Error fetching avatar for user ${userId}:`, err);
    return null;
  }
}

/**
 * Fetch both email and avatar for a single user
 * @param userId User UUID
 * @returns UserData object
 */
export async function fetchUserData(userId: string): Promise<UserData> {
  if (!userId) {
    return {
      user_id: userId,
      email: "Unknown",
      avatar_url: null,
      display_name: "Unknown",
    };
  }

  const userDataMap = await fetchUserDataBatch([userId]);
  return (
    userDataMap.get(userId) || {
      user_id: userId,
      email: "Unknown",
      avatar_url: null,
      display_name: "Unknown",
    }
  );
}

/**
 * Get email from current session user (fallback for when batch fetch doesn't return data)
 * @param userId User ID to check
 * @returns Email from session if user matches, undefined otherwise
 */
export async function getEmailFromSession(
  userId: string
): Promise<string | undefined> {
  try {
    const {
      data: { session },
    } = await auth.getSession();
    if (session?.user?.id === userId) {
      return session.user.email || undefined;
    }
  } catch {
    // Ignore session errors
  }
  return undefined;
}

